import { Props_SnippetAccordionAlternate } from "./AccordionAlternate.props";
import SnippetLink from "../Shared/Link/Link";
import { useState, type MouseEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CompileHeadingToSpan, CompileMarkdown } from "../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler";

const ANIMATION_DURATION = 0.3;

type AccordionAlternateRowProps = {
    accordionRow: Props_SnippetAccordionAlternate["repeatable_accordionRow"][number];
    index: number;
    isOpen: boolean;
    isDetailsOpen: boolean;
    onToggle: (index: number, event: MouseEvent) => void;
    onExitComplete: (index: number) => void;
};

const AccordionAlternateRow = ({
    accordionRow,
    index,
    isOpen,
    isDetailsOpen,
    onToggle,
    onExitComplete,
}: AccordionAlternateRowProps) => {
    const { linkText, linkHref, linkStyle, linkTarget, title, text, image, imageAlt, showImage } =
        accordionRow;

    return (
        <details
            key={"accordion-alternate-row-" + index}
            className={`snippet-accordion-alternate__row ${isOpen ? "snippet-accordion-alternate__row--open" : ""}`}
            open={isDetailsOpen}
        >
            <summary
                className="snippet-accordion-alternate__row-title"
                onClick={(e) => onToggle(index, e)}
            >
                {title?.value ? title.value : "Accordion Row " + (index + 1)}
            </summary>
            <AnimatePresence initial={false} onExitComplete={() => onExitComplete(index)}>
                {isOpen && (
                    <motion.div
                        className="snippet-accordion-alternate__content-container"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: ANIMATION_DURATION, ease: "easeInOut" }}
                    >
                        {!!image?.value && showImage?.value === true &&
                            <figure className="snippet-accordion-alternate__row-image-wrap">
                                <img
                                    className="snippet-accordion-alternate__row-image"
                                    src={image.value}
                                    alt={imageAlt?.value ?? ""}
                                    height="400"
                                    width="579"
                                    loading="lazy"
                                />
                            </figure>
                        }
                        {!!text?.value &&
                            <div className="snippet-accordion-alternate__text">
                                {CompileMarkdown(text.value)}
                            </div>
                        }
                        {!!(linkHref?.value && linkText?.value) &&
                            <SnippetLink
                                linkStyles={linkStyle?.value}
                                className="snippet-accordion-alternate__link"
                                url={linkHref.value}
                                openInNewTab={!!linkTarget?.value}
                                rel={linkTarget ? "noopener noreferrer" : undefined}
                            >
                                {linkText.value}
                            </SnippetLink>
                        }
                    </motion.div>
                )}
            </AnimatePresence>
        </details>
    );
};

const SnippetAccordionAlternate = (props: Props_SnippetAccordionAlternate) => {
    const { accordionTitle, repeatable_accordionRow, eyebrow, summary } = props;
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [detailsOpenSet, setDetailsOpenSet] = useState<Set<number>>(new Set());
    const accordionTitleClass = accordionTitle.visibility.value ? "snippet-accordion-alternate__heading" : "snippet-accordion-alternate__heading accessible-heading";

    const handleToggle = (index: number, event: MouseEvent) => {
        event.preventDefault();

        if (openIndex === index) {
            setOpenIndex(null);
        } else {
            setOpenIndex(index);
            setDetailsOpenSet((prev) => new Set(prev).add(index));
        }
    };

    const handleExitComplete = (index: number) => {
        setDetailsOpenSet((prev) => {
            const next = new Set(prev);
            next.delete(index);
            return next;
        });
    };

    return (
        <section className="snippet-accordion-alternate">
            {!!eyebrow?.text?.value && (
                <p className="snippet-accordion-alternate__eyebrow">{eyebrow.text.value}</p>
            )}
            <div className="snippet-accordion-alternate__container">
                <div className="snippet-accordion-alternate__heading-container">
                    {!!accordionTitle?.text?.value &&
                         <h2 className={`snippet-accordion-alternate__title ${accordionTitleClass}`}>
                        {CompileHeadingToSpan(accordionTitle.text.value)}
                    </h2>
                    }
                    {!!summary?.content?.value &&
                        <div className="snippet-accordion-alternate__summary">
                            {CompileMarkdown(summary.content.value)}
                        </div>
                    }
                </div>

                <div className="snippet-accordion-alternate__row-container">
                    {repeatable_accordionRow.map((accordionRow, index) => (
                        <AccordionAlternateRow
                            key={"accordion-alternate-row-" + index}
                            accordionRow={accordionRow}
                            index={index}
                            isOpen={openIndex === index}
                            isDetailsOpen={detailsOpenSet.has(index)}
                            onToggle={handleToggle}
                            onExitComplete={handleExitComplete}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export { SnippetAccordionAlternate };
